// User types for the system
export const USER_TYPES = {
  ADMIN: 'ADMIN',
  CONSULTANT: 'CONSULTANT',
  RECEPTIONIST: 'RECEPTIONIST',
  THERAPIST: 'THERAPIST',
} as const;

export type UserType = (typeof USER_TYPES)[keyof typeof USER_TYPES];

export const USER_TYPE_LABELS: Record<UserType, string> = {
  ADMIN: 'Admin',
  CONSULTANT: 'Consultant',
  // D27-5: Receptionist and Consultant are one rank; the OS calls both Consultant.
  RECEPTIONIST: 'Consultant',
  THERAPIST: 'Therapist',
};

// Feature access matrix
// true = full access, 'own' = own data only, false = no access
type AccessLevel = boolean | 'own';

interface FeatureAccess {
  dashboard: AccessLevel;
  schedule: AccessLevel;
  canBook: boolean;
  customers: boolean;
  staff: boolean;
  treatments: boolean;
  // Inventory P1. Not a legacy feature — inventory did not exist pre-RBAC — but
  // it needs a cell because `useGating.canFeature` falls back to this matrix
  // before resolved permissions load. Mirrors `treatments` exactly, matching the
  // backend's CATEGORY_TO_LEGACY_FEATURE['inventory'] = 'treatments'. If the two
  // sides disagreed, the nav would show a screen the API refuses (or hide one it
  // allows). Post-load visibility is by key (`inventory.view`), not by this cell.
  inventory: boolean;
  skillMatrix: boolean;
  rooms: boolean;
  commission: AccessLevel;
  commissionRates: boolean;
  reports: boolean;
  users: boolean;
  // Y-012: daily report card. Every staff confirms their own day.
  // Admins / HMs can view (but not confirm on behalf of) any staff.
  dailyReport: AccessLevel;
  // Phase 3 LEAVE: every staff submits + views their own leave ('own').
  // Admin gets 'true' (approval queue + entitlement config). Admin-only
  // actions (approve/reject/config) are additionally gated server-side via
  // require_admin and client-side via userType === 'ADMIN' checks.
  leave: AccessLevel;
  // Phase 3 SALARY: salary is sensitive (money). Admin gets 'true' (salary
  // runs, payslip drill-in across staff, structure/scheme/target/benefit
  // config). Every other staff gets 'own' — they only ever see their own
  // payslips via /salary/me and the confirm/dispute actions on them. All
  // cross-staff reads + every write are additionally gated server-side via
  // require_admin and client-side via userType === 'ADMIN' checks.
  salary: AccessLevel;
  // Phase 3 FINANCE / POS: finance is sensitive (money + system-of-record).
  // Admin gets `true` (POS checkout, ledger, daily close, approvals). Every
  // other role gets `false` in v1 — the dedicated POS-role decision is deferred
  // (§9 H FIN-H1). All Finance writes are additionally gated server-side via
  // require_admin + a `finance` feature key, and client-side via
  // userType === 'ADMIN' route checks.
  finance: AccessLevel;
}

const ACCESS_MATRIX: Record<UserType, FeatureAccess> = {
  ADMIN: {
    dashboard: true,
    schedule: true,
    canBook: true,
    customers: true,
    staff: true,
    treatments: true,
    inventory: true,
    skillMatrix: true,
    rooms: true,
    commission: true,
    commissionRates: true,
    reports: true,
    users: true,
    dailyReport: true,
    leave: true,
    salary: true,
    finance: true,
  },
  CONSULTANT: {
    dashboard: true,
    schedule: true,
    canBook: true,
    customers: true,
    staff: true,
    treatments: true,
    inventory: true,
    skillMatrix: true,
    rooms: true,
    commission: 'own',
    commissionRates: false,
    reports: false,
    users: false,
    dailyReport: 'own',
    leave: 'own',
    salary: 'own',
    finance: false,
  },
  RECEPTIONIST: {
    dashboard: true,
    schedule: true,
    canBook: true,
    customers: true,
    staff: true,
    treatments: true,
    inventory: true,
    skillMatrix: true,
    rooms: true,
    commission: 'own',
    commissionRates: false,
    reports: false,
    users: false,
    dailyReport: 'own',
    leave: 'own',
    salary: 'own',
    finance: false,
  },
  THERAPIST: {
    // Y-002 (2026-05-07): schedule + dashboard opened up — Yakson cultural
    // preference is "everyone sees what everyone's doing." Commission stays
    // 'own' (money is private, always). Y-012: dailyReport is 'own' — each
    // therapist confirms only their own day.
    dashboard: true,
    schedule: true,
    canBook: false,
    customers: false,
    staff: false,
    treatments: true,
    inventory: true,
    skillMatrix: false,
    rooms: false,
    commission: 'own',
    commissionRates: false,
    reports: false,
    users: false,
    dailyReport: 'own',
    leave: 'own',
    salary: 'own',
    finance: false,
  },
};

// Exported feature key type so callers (e.g. staff-filter) can request
// per-feature checks without re-importing FeatureAccess.
export type FeatureKey = keyof FeatureAccess;

// Y-067: features that support 'own data only' semantics. Used by both
// the Access Rights UI (to show/hide the 'own' option) and the PATCH
// endpoint (to reject 'own' overrides on boolean-only features, which
// would otherwise grant FULL access because canAccessFeature treats
// 'own' as truthy).
export const OWN_CAPABLE_FEATURES: ReadonlySet<keyof FeatureAccess> = new Set([
  'dashboard',
  'schedule',
  'commission',
  'dailyReport',
  'leave',
  'salary',
]);

// Route to feature mapping
const ROUTE_FEATURES: Record<string, keyof FeatureAccess> = {
  '/dashboard': 'dashboard',
  '/schedule': 'schedule',
  // Longest-prefix wins, so this beats '/customers'. Mirrors the same entry in
  // ROUTE_PERMISSIONS: the customer reports page gates on `reports`, like the
  // endpoint behind it.
  '/customers/reports': 'reports',
  '/customers': 'customers',
  '/staff': 'staff',
  '/treatments': 'treatments',
  '/rooms': 'rooms',
  '/commission': 'commission',
  // OPS-37: reads the Staff Roster, so it follows canBook (as ROUTE_PERMISSIONS).
  '/reports/duty-roster': 'canBook',
  '/reports': 'reports',
  '/settings': 'dashboard', // Everyone can access settings
  '/users': 'users',
  '/daily-report': 'dailyReport',
  '/leave': 'leave',
  // Both the admin salary surfaces and the staff /salary/me self-view map to
  // the same feature key. canAccessRoute treats 'own' as truthy so staff can
  // reach /salary/me; the admin-only pages additionally gate on
  // userType === 'ADMIN' client-side (and require_admin server-side).
  '/salary': 'salary',
  // Phase 3 FINANCE / POS: the whole /finance surface (POS checkout, ledger,
  // daily close, approvals) is admin-only in v1 (§9 H). canAccessRoute gates on
  // the `finance` feature; the pages additionally check userType === 'ADMIN'.
  '/finance': 'finance',
};

export function getFeatureAccess(userType: UserType, feature: keyof FeatureAccess): AccessLevel {
  return ACCESS_MATRIX[userType]?.[feature] ?? false;
}

// Y-067: exposed so /api/admin/access-rules can serialize defaults for
// cells that don't yet have a DB override.
export { ACCESS_MATRIX };
export type { FeatureAccess, AccessLevel };

export function canAccessFeature(userType: UserType, feature: keyof FeatureAccess): boolean {
  const access = getFeatureAccess(userType, feature);
  return access === true || access === 'own';
}

export function hasOwnDataOnly(userType: UserType, feature: keyof FeatureAccess): boolean {
  return getFeatureAccess(userType, feature) === 'own';
}

export function canAccessRoute(userType: UserType, pathname: string): boolean {
  // Find matching route - check most specific first
  const routeKey = Object.keys(ROUTE_FEATURES)
    .sort((a, b) => b.length - a.length)
    .find((route) => pathname.startsWith(route) || pathname === route);

  if (!routeKey) return true; // Unknown route - allow (will 404 anyway)

  const feature = ROUTE_FEATURES[routeKey];
  return canAccessFeature(userType, feature);
}

// Helper for sidebar navigation
export function getVisibleNavItems(userType: UserType): string[] {
  const items: string[] = ['dashboard', 'schedule'];

  if (canAccessFeature(userType, 'customers')) items.push('customers');
  if (canAccessFeature(userType, 'staff')) items.push('staff');
  items.push('treatments'); // Everyone can see treatments
  if (canAccessFeature(userType, 'rooms')) items.push('rooms');
  if (canAccessFeature(userType, 'commission')) items.push('commission');
  if (canAccessFeature(userType, 'reports')) items.push('reports');
  if (canAccessFeature(userType, 'users')) items.push('users');
  if (canAccessFeature(userType, 'leave')) items.push('leave');
  if (canAccessFeature(userType, 'salary')) items.push('salary');
  if (canAccessFeature(userType, 'finance')) items.push('finance');

  return items;
}

// Check if user can book appointments
export function canBook(userType: UserType): boolean {
  return canAccessFeature(userType, 'canBook');
}

// Check if user can see commission rates
export function canSeeCommissionRates(userType: UserType): boolean {
  return canAccessFeature(userType, 'commissionRates');
}

// Check if user can see skill matrix
export function canSeeSkillMatrix(userType: UserType): boolean {
  return canAccessFeature(userType, 'skillMatrix');
}
